import {
  Alert, FormField, Input, Select, SpaceBetween, Toggle,
} from '@cloudscape-design/components';
import { deriveSlug } from '../fmBundles';
import { TEAM_OPTIONS } from '../productSchemas';
import { Errors, FmWizardState, NEW_ENV_VALUE, SERVICE_NAME_MAX } from './types';

interface ProjectOption { documentId: string; name: string; }
interface EnvOption { documentId: string; name: string; isProduction: boolean; }

interface Props {
  state: FmWizardState;
  errors: Errors;
  projects: ProjectOption[];
  environments: EnvOption[];
  envLoading: boolean;
  onProjectChange: (documentId: string) => void;
  patch: (p: Partial<FmWizardState>) => void;
}

export default function BundleInfoStep({
  state, errors, projects, environments, envLoading, onProjectChange, patch,
}: Props) {
  const projectSlug = deriveSlug(state.projectName);
  const envOptions = [
    ...environments.map((e) => ({
      label: `${e.name}${e.isProduction ? ' (production)' : ''}`,
      value: e.documentId,
    })),
    { label: 'Create new environment…', value: NEW_ENV_VALUE },
  ];

  const onServiceNameChange = (value: string) =>
    patch({ serviceName: value, serviceNameTouched: true });

  return (
    <SpaceBetween size="m">
      <FormField label="Project" description="The project this FM bundle belongs to." errorText={errors.projectId}>
        <Select
          selectedOption={state.projectId ? { label: state.projectName, value: state.projectId } : null}
          onChange={({ detail }) => onProjectChange(detail.selectedOption.value!)}
          options={projects.map((p) => ({ label: p.name, value: p.documentId }))}
          placeholder="Select a project"
          filteringType="auto"
        />
      </FormField>

      <FormField
        label="Environment"
        description="Environment to deploy into. Environments are shared across the whole project."
        errorText={errors.environmentId}
      >
        <Select
          selectedOption={envOptions.find((o) => o.value === state.environmentId) ?? null}
          onChange={({ detail }) => patch({ environmentId: detail.selectedOption.value! })}
          options={envOptions}
          placeholder="Select an environment"
          disabled={!state.projectId}
          statusType={envLoading ? 'loading' : 'finished'}
          loadingText="Loading environments"
        />
      </FormField>

      {state.environmentId === NEW_ENV_VALUE && (
        <>
          <FormField label="New environment name" description="Name for the new environment to create." errorText={errors.newEnvName}>
            <Input
              value={state.newEnvName}
              onChange={({ detail }) => patch({ newEnvName: detail.value })}
              placeholder="e.g. staging"
            />
          </FormField>
          <FormField label="Production environment" description="Mark this environment as production.">
            <Toggle checked={state.newEnvIsProduction} onChange={({ detail }) => patch({ newEnvIsProduction: detail.checked })}>
              {state.newEnvIsProduction ? 'Production' : 'Non-production'}
            </Toggle>
          </FormField>
          <Alert type="info">
            Environments are shared by the whole project (stacks, products, diagrams). Reuse an existing one when it has the same meaning.
          </Alert>
        </>
      )}

      <FormField label="Team" description="Team that owns this bundle. Sent as the ICLTeam tag." errorText={errors.team}>
        <Select
          selectedOption={state.team ? { label: state.team, value: state.team } : null}
          onChange={({ detail }) => patch({ team: detail.selectedOption.value! })}
          options={TEAM_OPTIONS}
          placeholder="Select a team"
        />
      </FormField>

      <FormField label="Project slug" description="Derived from the project name; used in resource names and tags.">
        <Input value={projectSlug} readOnly disabled />
      </FormField>

      <FormField
        label="Service name"
        description="Used as the runbook ProjectName: it names the AWS role, policy, budget and inference profiles, and is the ICLProject tag value. Lowercase letters, digits and hyphens, max 30 characters."
        constraintText={`${state.serviceName.length}/${SERVICE_NAME_MAX}`}
        errorText={errors.serviceName}
      >
        <Input value={state.serviceName} onChange={({ detail }) => onServiceNameChange(detail.value)} />
      </FormField>

      <Alert type="info">
        A service name belongs to one project. The same service name can be reused in other
        environments of this project, but not by a different project.
      </Alert>
    </SpaceBetween>
  );
}
